import { useOrganization } from "@clerk/clerk-react";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import FormField from "../atoms/FormField";
import FormSelect from "../atoms/FormSelect";
import ContentBlockEditor from "../molecules/ContentBlockEditor";
import FileUploadField from "../molecules/FileUploadField";

/**
 * WorkshopForm - Form for creating workshop ideas
 *
 * @param {Object} props
 * @param {Function} props.onSubmit - Form submission handler
 * @param {boolean} props.isSubmitting - Whether form is submitting
 */
const WorkshopForm = forwardRef(({ onSubmit, isSubmitting = false }, ref) => {
  const [blocks, setBlocks] = useState([{ id: 1, content: "", type: "text" }]);
  const [title, setTitle] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);

  const { organization } = useOrganization();

  // Expose handleSubmit method to parent component
  useImperativeHandle(ref, () => ({
    handleSubmit: () => {
      // Combine blocks into a description
      const description = blocks
        .map((block) => {
          if (block.type === "heading") {
            return `## ${block.content}`;
          } else if (block.type === "list") {
            return block.content
              .split("\n")
              .map((item) => `- ${item}`)
              .join("\n");
          }
          return block.content;
        })
        .join("\n\n");

      // Call parent's onSubmit with form data
      onSubmit({
        title,
        description,
        assigneeId,
        selectedFiles,
      });
    },
  }));

  // Block management handlers
  const handleAddBlock = (type = "text") => {
    const newId = blocks.length ? Math.max(...blocks.map((b) => b.id)) + 1 : 1;
    setBlocks([...blocks, { id: newId, content: "", type }]);
  };

  const handleRemoveBlock = (id) => {
    if (blocks.length > 1) {
      setBlocks(blocks.filter((block) => block.id !== id));
    }
  };

  const handleBlockChange = (id, content) => {
    setBlocks(
      blocks.map((block) => (block.id === id ? { ...block, content } : block))
    );
  };

  const handleTypeChange = (id, type) => {
    setBlocks(
      blocks.map((block) => (block.id === id ? { ...block, type } : block))
    );
  };

  // Create options for assignee dropdown
  const assigneeOptions = [
    { value: "", label: "Unassigned" },
    ...(organization?.memberships?.map((membership) => ({
      value: membership.publicUserData.userId,
      label: `${membership.publicUserData.firstName} ${membership.publicUserData.lastName}`,
    })) || []),
  ];

  return (
    <div className="space-y-6">
      <FormField
        id="title"
        label="Idea Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Name your idea"
        required
      />

      <FormSelect
        id="assignee"
        label="Assignee"
        value={assigneeId}
        onChange={(e) => setAssigneeId(e.target.value)}
        options={assigneeOptions}
      />

      <FileUploadField
        id="file"
        label="Attachments"
        selectedFiles={selectedFiles}
        onChange={setSelectedFiles}
        multiple
      />

      <ContentBlockEditor
        blocks={blocks}
        onAddBlock={handleAddBlock}
        onRemoveBlock={handleRemoveBlock}
        onBlockChange={handleBlockChange}
        onTypeChange={handleTypeChange}
      />
    </div>
  );
});

export default WorkshopForm;
